// Ten creepypasta figures, each built from three.js primitives only — no
// loaders, no textures, no external assets. They live in the steam-gloom of the
// sauna and read by their silhouette and a little sickly emissive bleed.
//
// Conventions (shared with the rest of the client):
//   - Human scale: a standing figure is ~1.7m tall.
//   - Origin at the FLOOR: feet at y=0, facing -z. The caller positions the
//     group; builders never translate the whole group.
//   - update(t, dread): idle creepiness that INTENSIFIES with dread (0..1) —
//     faster tremor, brighter/pulsing emissive, a slight lurch toward the
//     viewer. No geometry rebuilds per frame; we only mutate transforms and
//     material.emissiveIntensity.
//
// The horror is in the WRONGNESS: asymmetry, too many or too few features,
// joints bent the wrong way, sunken or extra eyes, elongation, sag, and an
// unnatural stillness that snaps into a twitch.

import THREE from "../three.js";
import type { Group, Material } from "three";

export interface CreepyAvatar {
  group: Group;
  update: (t: number, dread: number) => void;
}

export type Archetype =
  | "face"
  | "burnt"
  | "child"
  | "tall"
  | "crawler"
  | "shadow"
  | "false_bather"
  | "limbs"
  | "voice"
  | "steamform";

// --- shared palette -------------------------------------------------------

const SKIN_DEAD = 0x8f8478; // grey, drowned-looking flesh
const SKIN_GREY = 0x726a60;
const SKIN_DARK = 0x3a342d;
const CHAR = 0x130b08; // burnt
const EMBER = 0xff5a1e;
const BLOOD = 0x661111; // deep red
const BILE = 0x2e3b14; // sickly green
const BRUISE = 0x2a1133; // bruise purple
const EYE_BLACK = 0x040404;

// CapsuleGeometry only landed in later r12x/r13x; the classic global build may
// not have it. Fall back to a stretched cylinder so builders never crash.
function capsule(radius: number, length: number, mat: Material): THREE.Mesh {
  const Cap = (THREE as unknown as { CapsuleGeometry?: unknown }).CapsuleGeometry;
  if (typeof Cap === "function") {
    return new THREE.Mesh(
      new (Cap as new (r: number, l: number, cs: number, rs: number) => THREE.BufferGeometry)(
        radius,
        length,
        4,
        8,
      ),
      mat,
    );
  }
  return new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length + radius, 10), mat);
}

function flesh(color: number, emissive: number, emissiveIntensity = 0.35): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness: 1, metalness: 0, emissive, emissiveIntensity });
}

/** A deep, lightless eye socket sphere. */
function eye(radius = 0.03): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.SphereGeometry(radius, 10, 10),
    new THREE.MeshStandardMaterial({ color: EYE_BLACK, roughness: 1, emissive: 0x000000 }),
  );
}

/** A soft dark disc laid flat on the floor, so a figure reads as standing ON the
 *  boards instead of hovering over them. Cheap fake contact shadow. */
function groundShadow(radius = 0.4): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.CircleGeometry(radius, 20),
    new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.45, depthWrite: false }),
  );
  m.rotation.x = -Math.PI / 2;
  m.position.y = 0.012;
  return m;
}

/** A ring of small teeth around a mouth opening — points downward from the upper
 *  lip. Returns a group centred on the mouth; caller positions/rotates it. */
function teeth(spanX: number, count: number, len = 0.02, color = 0xd8c7b0): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.7, emissive: 0x1a1410, emissiveIntensity: 0.2 });
  for (let i = 0; i < count; i++) {
    const tooth = new THREE.Mesh(new THREE.ConeGeometry(len * 0.35, len, 6), mat);
    tooth.position.x = (i / (count - 1) - 0.5) * spanX;
    tooth.rotation.x = Math.PI; // tip down
    g.add(tooth);
  }
  return g;
}

// =========================================================================
// face — the gaunt face + splayed hands pressed to the glass
// =========================================================================

function buildFace(): CreepyAvatar {
  const group = new THREE.Group();
  const inner = new THREE.Group(); // the pressable bits
  inner.position.y = 1.5; // head height; this one hangs at a window
  group.add(inner);

  const skin = flesh(SKIN_DEAD, 0x3a2418, 0.6);

  // A cold outside light so the face reads through dark glass.
  const glow = new THREE.PointLight(0x6f86a6, 0.5, 1.6, 1.5);
  glow.position.set(0, 0, 0.3);
  inner.add(glow);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 22, 22), skin);
  head.scale.set(0.92, 1.35, 0.8); // gaunt, long-jawed
  inner.add(head);

  // Sunken cheeks: two darker depressions flanking the nose.
  const hollowMat = flesh(SKIN_DARK, 0x1a0d08, 0.4);
  for (const sx of [-1, 1]) {
    const cheek = new THREE.Mesh(new THREE.SphereGeometry(0.07, 12, 12), hollowMat);
    cheek.scale.set(0.7, 1.1, 0.5);
    cheek.position.set(sx * 0.09, -0.02, 0.1);
    inner.add(cheek);
  }

  // Eyes pushed too deep and too close together; the left one slightly higher.
  const eL = eye(0.034);
  eL.position.set(-0.05, 0.06, 0.11);
  const eR = eye(0.034);
  eR.position.set(0.045, 0.04, 0.11);
  inner.add(eL, eR);

  // A wide, lipless mouth — too wide.
  const mouth = new THREE.Mesh(
    new THREE.SphereGeometry(0.06, 14, 12),
    new THREE.MeshStandardMaterial({ color: 0x120303, roughness: 1, emissive: BLOOD, emissiveIntensity: 0.25 }),
  );
  mouth.scale.set(1.6, 0.5, 0.4);
  mouth.position.set(0.01, -0.13, 0.12);
  inner.add(mouth);

  // A row of small crooked teeth set into the upper edge of that too-wide mouth.
  const upperTeeth = teeth(0.16, 9, 0.022, 0x7a6a58);
  upperTeeth.position.set(0.01, -0.105, 0.15);
  inner.add(upperTeeth);
  const lowerTeeth = teeth(0.14, 8, 0.018, 0x6a5a48);
  lowerTeeth.rotation.z = Math.PI; // point up from the lower lip
  lowerTeeth.position.set(0.01, -0.155, 0.15);
  inner.add(lowerTeeth);

  // Two splayed hands pressed flat to the glass, fingers spread.
  const hands: THREE.Group[] = [];
  for (const sx of [-1, 1]) {
    const hand = new THREE.Group();
    const palm = new THREE.Mesh(new THREE.SphereGeometry(0.06, 10, 10), skin);
    palm.scale.set(1.1, 1.3, 0.28);
    hand.add(palm);
    for (let f = 0; f < 5; f++) {
      const finger = capsule(0.012, 0.07, skin);
      const a = (f - 2) * 0.42;
      finger.position.set(Math.sin(a) * 0.07, 0.09, 0);
      finger.rotation.z = -a;
      finger.scale.z = 0.4;
      hand.add(finger);
    }
    hand.position.set(sx * 0.26, -0.06, 0.08);
    hands.push(hand);
    inner.add(hand);
  }

  return {
    group,
    update: (t, dread) => {
      // Presses toward the glass as dread climbs; only the inner bits move so
      // the root stays parked on the window plane.
      const press = 0.1 * dread + 0.015 * Math.sin(t * 1.3);
      inner.position.z = press;
      head.scale.z = 0.8 + dread * 0.3;
      // A slow head-tilt that quickens with dread, plus a rare hard twitch.
      const twitch = dread > 0.5 && Math.sin(t * 11) > 0.97 ? 0.12 : 0;
      inner.rotation.z = Math.sin(t * (0.7 + dread * 2)) * 0.05 + twitch;
      glow.intensity = 0.4 + dread * 0.5 + 0.1 * Math.sin(t * 4);
      skin.emissiveIntensity = 0.5 + dread * 0.5;
      for (const h of hands) h.position.z = 0.04 - press * 0.5;
    },
  };
}

// =========================================================================
// burnt — charred, steaming humanoid with glowing cracks
// =========================================================================

function buildBurntMan(): CreepyAvatar {
  const group = new THREE.Group();
  const char = flesh(CHAR, EMBER, 0.4);

  const hips = new THREE.Group();
  hips.position.y = 0.9;
  group.add(hips);

  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.21, 0.7, 12), char);
  torso.position.y = 0.35;
  hips.add(torso);

  // Shoulders fused, asymmetric — the right one slumps lower.
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 18, 18), char);
  head.scale.set(0.95, 1.05, 0.95);
  head.position.set(0.02, 0.82, 0);
  hips.add(head);

  // A jaw that has burnt half-open.
  const jaw = new THREE.Mesh(new THREE.SphereGeometry(0.07, 10, 10), char);
  jaw.scale.set(1.1, 0.5, 0.7);
  jaw.position.set(0, 0.72, 0.06);
  hips.add(jaw);

  // Arms — the right one longer, hanging.
  const arms: THREE.Mesh[] = [];
  for (const sx of [-1, 1]) {
    const arm = capsule(0.05, sx > 0 ? 0.6 : 0.5, char);
    arm.position.set(sx * 0.21, 0.4, 0);
    arm.rotation.z = sx * 0.18;
    arms.push(arm);
    hips.add(arm);
  }

  // Legs.
  for (const sx of [-1, 1]) {
    const leg = capsule(0.06, 0.7, char);
    leg.position.set(sx * 0.08, -0.45, 0);
    hips.add(leg);
  }

  // Glowing cracks scattered over the body, each its own emissive sliver.
  const crackMat = new THREE.MeshStandardMaterial({
    color: 0x200a02,
    roughness: 1,
    emissive: EMBER,
    emissiveIntensity: 1.2,
  });
  const cracks: THREE.Mesh[] = [];
  for (let i = 0; i < 9; i++) {
    const crack = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.12 + Math.random() * 0.16, 0.018), crackMat);
    const a = Math.random() * Math.PI * 2;
    const h = 0.1 + Math.random() * 0.6;
    crack.position.set(Math.cos(a) * 0.16, h, Math.sin(a) * 0.16 + 0.02);
    crack.rotation.z = (Math.random() - 0.5) * 1.2;
    cracks.push(crack);
    hips.add(crack);
  }

  // A ragged row of teeth in the burnt-open jaw, glowing like the cracks.
  const jawTeeth = teeth(0.1, 7, 0.02, 0x3a1206);
  (jawTeeth.children as THREE.Mesh[]).forEach((m) => {
    (m.material as THREE.MeshStandardMaterial).emissive.setHex(EMBER);
    (m.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.7;
  });
  jawTeeth.position.set(0, 0.78, 0.1);
  hips.add(jawTeeth);

  // Heat shimmer above the figure: a faint upward ember light.
  const heat = new THREE.PointLight(0xff3a00, 0.4, 1.2, 2);
  heat.position.set(0, 0.5, 0);
  hips.add(heat);

  // A couple of smoke wisps rising off the shoulders — soft translucent planes.
  const smokeMat = new THREE.MeshStandardMaterial({ color: 0x1a1614, transparent: true, opacity: 0.18, depthWrite: false });
  const smoke: THREE.Mesh[] = [];
  for (const sx of [-1, 1]) {
    const w = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.5, 8), smokeMat.clone());
    w.position.set(sx * 0.12, 1.0, 0);
    smoke.push(w);
    hips.add(w);
  }

  // Charred figure stands on scorched boards.
  group.add(groundShadow(0.42));

  return {
    group,
    update: (t, dread) => {
      // Seething: cracks pulse, faster and brighter under dread.
      const pulse = 0.6 + 0.4 * Math.sin(t * (3 + dread * 4));
      crackMat.emissiveIntensity = (0.8 + dread * 1.4) * pulse;
      char.emissiveIntensity = 0.3 + dread * 0.4 + 0.1 * pulse;
      heat.intensity = 0.3 + dread * 0.6 + 0.15 * Math.sin(t * 7);
      // A slow rotating sway as if the air itself burns; lurch toward viewer.
      hips.rotation.z = Math.sin(t * 1.1) * 0.03 * (1 + dread);
      hips.position.z = dread * 0.12 + 0.01 * Math.sin(t * 2.3);
      head.rotation.y = Math.sin(t * 0.6) * 0.15;
      // The burnt jaw works slowly, as if trying to speak through the char.
      jaw.position.y = 0.72 - 0.03 * (0.5 + 0.5 * Math.sin(t * (1.5 + dread * 2)));
      jawTeeth.position.y = 0.78 - 0.015 * (0.5 + 0.5 * Math.sin(t * (1.5 + dread * 2)));
      // Smoke breathes upward and dissipates, faster with dread.
      for (let i = 0; i < smoke.length; i++) {
        const m = smoke[i].material as THREE.MeshStandardMaterial;
        const ph = (t * (0.3 + dread * 0.3) + i * 0.5) % 1;
        smoke[i].position.y = 1.0 + ph * 0.6;
        m.opacity = 0.22 * (1 - ph);
        smoke[i].scale.setScalar(0.6 + ph);
      }
    },
  };
}

// =========================================================================
// child — small, too still, oversized head tilted up at you
// =========================================================================

function buildChild(): CreepyAvatar {
  const group = new THREE.Group();
  const skin = flesh(SKIN_DEAD, 0x241218, 0.3);

  const body = new THREE.Group();
  group.add(body);

  // Small torso (whole figure ~1.0m).
  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 0.34, 10), skin);
  torso.position.y = 0.55;
  body.add(torso);

  // Oversized head, tilted up — far too big for the body.
  const headPivot = new THREE.Group();
  headPivot.position.y = 0.74;
  body.add(headPivot);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.17, 20, 20), skin);
  head.scale.set(1, 1.05, 0.95);
  head.position.y = 0.13;
  headPivot.add(head);
  headPivot.rotation.x = -0.4; // chin up, staring up at the viewer

  // Two big black eyes, set a touch too far apart and too low on the face.
  for (const sx of [-1, 1]) {
    const e = eye(0.045);
    e.position.set(sx * 0.075, 0.1, 0.15);
    headPivot.add(e);
  }
  // A small, closed, downturned mouth — wrong against the upturned face.
  const mouth = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.01, 0.01), flesh(0x1a0808, BLOOD, 0.2));
  mouth.position.set(0, 0.04, 0.16);
  headPivot.add(mouth);

  // Stubby arms hanging dead-straight at the sides.
  for (const sx of [-1, 1]) {
    const arm = capsule(0.035, 0.3, skin);
    arm.position.set(sx * 0.13, 0.5, 0);
    body.add(arm);
  }
  // Stubby legs.
  for (const sx of [-1, 1]) {
    const leg = capsule(0.04, 0.34, skin);
    leg.position.set(sx * 0.06, 0.18, 0);
    body.add(leg);
  }

  // Lank wet hair: a curtain of thin dark strands hanging off the big head,
  // longer at the front so it half-curtains the staring eyes.
  const hairMat = flesh(0x0d0a0a, 0x000000, 0);
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2;
    const front = Math.cos(a) > 0.2 ? 0.05 : 0;
    const strand = new THREE.Mesh(new THREE.CylinderGeometry(0.004, 0.002, 0.16 + front, 5), hairMat);
    strand.position.set(Math.cos(a) * 0.15, 0.16, 0.13 + Math.sin(a) * 0.13);
    strand.rotation.x = 0.2;
    headPivot.add(strand);
  }

  // It stands on the boards.
  group.add(groundShadow(0.3));

  return {
    group,
    update: (t, dread) => {
      // The horror is the stillness. Almost nothing moves... until a sudden,
      // unnatural snap of the head when dread is high.
      const snap = dread > 0.6 && Math.sin(t * 9) > 0.985 ? (Math.random() - 0.5) * 0.5 : 0;
      headPivot.rotation.z = snap;
      headPivot.rotation.x = -0.4 - dread * 0.15; // tilts further up at you
      // Barely-there breathing, and a slow uneasy rock as dread climbs.
      body.scale.y = 1 + 0.004 * Math.sin(t * 1.2);
      body.rotation.z = dread * 0.04 * Math.sin(t * 0.8);
      skin.emissiveIntensity = 0.25 + dread * 0.4;
      body.position.z = dread * 0.08; // creeps closer
    },
  };
}

// =========================================================================
// tall — ~2.3m, impossibly thin, stooped, head bent under the ceiling
// =========================================================================

function buildTall(): CreepyAvatar {
  const group = new THREE.Group();
  const skin = flesh(SKIN_GREY, BRUISE, 0.3);

  // Long thin legs.
  for (const sx of [-1, 1]) {
    const leg = capsule(0.05, 1.2, skin);
    leg.position.set(sx * 0.07, 0.65, 0);
    group.add(leg);
  }

  // Stoop pivot at the top of the legs; everything above bends forward.
  const upper = new THREE.Group();
  upper.position.y = 1.25;
  upper.rotation.x = 0.5; // stooped forward, hunched
  group.add(upper);

  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 0.85, 10), skin);
  torso.position.y = 0.42;
  upper.add(torso);

  // Impossibly long arms that nearly reach the floor, each ending in a spread of
  // long thin claw-fingers.
  const arms: THREE.Mesh[] = [];
  for (const sx of [-1, 1]) {
    const arm = capsule(0.035, 1.0, skin);
    arm.position.set(sx * 0.12, 0.35, 0.02);
    arm.rotation.z = sx * 0.06;
    arms.push(arm);
    upper.add(arm);
    const claw = new THREE.Group();
    claw.position.set(sx * 0.15, -0.18, 0.02);
    for (let f = 0; f < 4; f++) {
      const finger = capsule(0.01, 0.13, skin);
      finger.position.x = (f - 1.5) * 0.025;
      finger.rotation.x = 0.25;
      claw.add(finger);
    }
    upper.add(claw);
  }

  // Neck bent so the head hangs under an unseen ceiling, facing down/forward.
  const neck = new THREE.Group();
  neck.position.y = 0.85;
  neck.rotation.x = 0.7;
  upper.add(neck);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.11, 18, 18), skin);
  head.scale.set(0.8, 1.4, 0.8); // long narrow skull
  head.position.y = 0.12;
  neck.add(head);

  // No eyes — just smooth pale dread, with a single dark vertical seam.
  const seam = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.14, 0.01), flesh(0x0a0a0a, 0x000000, 0));
  seam.position.set(0, 0.12, 0.09);
  neck.add(seam);

  // It stands on the boards (a wide, faint pool — it's spread over two metres).
  group.add(groundShadow(0.5));

  return {
    group,
    update: (t, dread) => {
      // A slow, swaying overhead presence; the stoop deepens as it leans in.
      upper.rotation.x = 0.5 + 0.08 * Math.sin(t * 0.7) + dread * 0.12;
      upper.rotation.z = 0.04 * Math.sin(t * 0.5);
      neck.rotation.x = 0.7 + 0.1 * Math.sin(t * 0.9 + 1);
      // Long arms drift and reach as dread rises.
      for (let i = 0; i < arms.length; i++) {
        arms[i].rotation.x = -dread * 0.4 + 0.05 * Math.sin(t * 1.3 + i);
      }
      skin.emissiveIntensity = 0.25 + dread * 0.45 + 0.05 * Math.sin(t * 3);
      group.position.z = dread * 0.15; // looms closer
    },
  };
}

// =========================================================================
// crawler — low quadruped, wrong joints, arched spine, head low
// =========================================================================

function buildCrawler(): CreepyAvatar {
  const group = new THREE.Group();
  const skin = flesh(SKIN_DARK, BILE, 0.3);

  // Arched spine: a torso tube rotated so the back humps upward.
  const spine = new THREE.Group();
  spine.position.y = 0.5;
  group.add(spine);

  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.13, 0.6, 12), skin);
  torso.rotation.z = Math.PI / 2; // horizontal
  // Bow it: scale to suggest an arched back via a second hump on top.
  spine.add(torso);
  const hump = new THREE.Mesh(new THREE.SphereGeometry(0.16, 14, 12), skin);
  hump.scale.set(1.4, 0.7, 1);
  hump.position.set(0, 0.08, 0);
  spine.add(hump);

  // A row of vertebrae pushing up through the skin of the arched back.
  const boneMat = flesh(0x6b6258, 0x140f0a, 0.25);
  for (let i = 0; i < 7; i++) {
    const z = (i / 6 - 0.5) * 0.5;
    const knob = new THREE.Mesh(new THREE.SphereGeometry(0.028 - Math.abs(z) * 0.02, 8, 8), boneMat);
    knob.position.set(0, 0.18 - z * z * 0.6, z);
    spine.add(knob);
  }

  // Head hangs low and forward off the front of the spine.
  const neck = new THREE.Group();
  neck.position.set(0, -0.05, -0.32);
  neck.rotation.x = 0.6;
  spine.add(neck);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.1, 16, 16), skin);
  head.scale.set(0.9, 0.8, 1.3); // long muzzle-like skull, pointing down
  head.position.set(0, -0.08, -0.05);
  neck.add(head);
  // Eyes set on top of the head, looking up — wrong.
  for (const sx of [-1, 1]) {
    const e = eye(0.028);
    e.position.set(sx * 0.05, 0.02, -0.1);
    neck.add(e);
  }

  // Four limbs with joints bent the wrong way (knees inverting upward).
  const legs: THREE.Group[] = [];
  const offs: [number, number][] = [
    [-0.12, -0.28],
    [0.12, -0.28],
    [-0.12, 0.28],
    [0.12, 0.28],
  ];
  for (const [x, z] of offs) {
    const hip = new THREE.Group();
    hip.position.set(x, -0.02, z);
    const thigh = capsule(0.035, 0.32, skin);
    thigh.position.y = -0.1;
    thigh.rotation.x = -0.9; // splays outward at a broken angle
    hip.add(thigh);
    const shin = capsule(0.03, 0.34, skin);
    shin.position.set(0, -0.32, 0.18);
    shin.rotation.x = 1.6; // bent the WRONG way — knee folds up and back
    hip.add(shin);
    spine.add(hip);
    legs.push(hip);
  }

  // Low, wide contact shadow — it's splayed across the floor.
  group.add(groundShadow(0.45));

  return {
    group,
    update: (t, dread) => {
      // Skittering tremor that intensifies; the spine arches and flattens.
      const r = 1 + dread;
      spine.rotation.x = -0.05 + 0.04 * Math.sin(t * 2.2 * r);
      hump.scale.y = 0.7 + 0.08 * Math.abs(Math.sin(t * 3 * r));
      neck.rotation.x = 0.6 + 0.15 * Math.sin(t * 1.7);
      // Twitchy legs.
      for (let i = 0; i < legs.length; i++) {
        legs[i].rotation.z = 0.08 * Math.sin(t * (4 + dread * 6) + i * 1.6);
      }
      skin.emissiveIntensity = 0.25 + dread * 0.4;
      group.position.z = dread * 0.18; // scrabbles toward you
    },
  };
}

// =========================================================================
// shadow — near-black matte silhouette that drinks light; two pinprick eyes
// =========================================================================

function buildShadow(): CreepyAvatar {
  const group = new THREE.Group();
  // Color near-black, roughness 1, no emissive on the body: it absorbs light.
  const dark = new THREE.MeshStandardMaterial({
    color: 0x010101,
    roughness: 1,
    metalness: 0,
    emissive: 0x000000,
  });

  const body = new THREE.Group();
  group.add(body);

  // A simple, slightly-too-broad humanoid mass — no detail, just absence.
  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, 0.95, 14), dark);
  torso.position.y = 0.95;
  body.add(torso);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.17, 16, 16), dark);
  head.position.y = 1.55;
  body.add(head);
  // Shoulders bleeding into arms, no hands.
  for (const sx of [-1, 1]) {
    const arm = capsule(0.07, 0.7, dark);
    arm.position.set(sx * 0.24, 0.95, 0);
    arm.rotation.z = sx * 0.1;
    body.add(arm);
  }
  // Legs taper into a smear that doesn't quite reach the floor — it hovers.
  const legs = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.7, 14), dark);
  legs.rotation.x = Math.PI;
  legs.position.y = 0.4;
  body.add(legs);

  // The only light it gives off: two faint pinprick eyes.
  const eyeMat = new THREE.MeshStandardMaterial({
    color: 0x000000,
    emissive: 0xffffff,
    emissiveIntensity: 0.4,
  });
  const eyes: THREE.Mesh[] = [];
  for (const sx of [-1, 1]) {
    const e = new THREE.Mesh(new THREE.SphereGeometry(0.012, 8, 8), eyeMat);
    e.position.set(sx * 0.05, 1.58, 0.16);
    eyes.push(e);
    body.add(e);
  }

  return {
    group,
    update: (t, dread) => {
      // Almost no body motion — it is unnervingly still — but the eyes brighten
      // and the whole mass slides forward as dread climbs.
      eyeMat.emissiveIntensity = 0.3 + dread * 0.9 + (Math.sin(t * 13) > 0.9 ? 0.6 : 0); // rare flare
      body.position.y = 0.02 * Math.sin(t * 0.6); // faint hover
      body.rotation.y = 0.03 * Math.sin(t * 0.4);
      body.position.z = dread * 0.2;
      for (const e of eyes) e.scale.setScalar(1 + dread * 0.4);
    },
  };
}

// =========================================================================
// false_bather — an ordinary person, subtly, deeply wrong
// =========================================================================

function buildFalseBather(): CreepyAvatar {
  const group = new THREE.Group();
  const skin = flesh(SKIN_DEAD, 0x2a1a14, 0.25);

  // A perfectly ordinary seated posture: hips on a bench at ~0.45m.
  const body = new THREE.Group();
  body.position.y = 0.45;
  group.add(body);

  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.18, 0.6, 14), skin);
  torso.position.y = 0.3;
  body.add(torso);

  // Thighs resting forward (seated).
  for (const sx of [-1, 1]) {
    const thigh = capsule(0.07, 0.4, skin);
    thigh.rotation.x = Math.PI / 2;
    thigh.position.set(sx * 0.09, 0, 0.2);
    body.add(thigh);
    const shin = capsule(0.06, 0.42, skin);
    shin.position.set(sx * 0.09, -0.22, 0.38);
    body.add(shin);
  }
  // Hands resting on knees — but the LEFT hand has six fingers (the one detail).
  for (const sx of [-1, 1]) {
    const hand = new THREE.Group();
    const palm = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.03, 0.1), skin);
    hand.add(palm);
    const nFingers = sx < 0 ? 6 : 5; // the wrongness, easily missed
    for (let f = 0; f < nFingers; f++) {
      const finger = capsule(0.012, 0.05, skin);
      finger.position.set((f - (nFingers - 1) / 2) * 0.018, 0, 0.07);
      finger.rotation.x = Math.PI / 2;
      hand.add(finger);
    }
    hand.position.set(sx * 0.09, -0.04, 0.4);
    body.add(hand);
  }

  // Arms.
  for (const sx of [-1, 1]) {
    const arm = capsule(0.05, 0.45, skin);
    arm.position.set(sx * 0.18, 0.3, 0.18);
    arm.rotation.x = 0.7;
    body.add(arm);
  }

  // The head, on a neck that lets it rotate slightly too far.
  const neck = new THREE.Group();
  neck.position.y = 0.62;
  body.add(neck);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 20, 20), skin);
  neck.add(head);
  // A blank, smooth face: a faint nose ridge, no eyes, no mouth.
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.02, 0.06, 8), skin);
  nose.rotation.x = Math.PI / 2;
  nose.position.set(0, -0.01, 0.14);
  neck.add(nose);
  neck.rotation.y = 0.9; // head turned slightly too far to start

  // It sits on the bench, feet on the boards.
  group.add(groundShadow(0.36));

  return {
    group,
    update: (t, dread) => {
      // It sits like a real person — for a while. The head rotation creeps
      // past what a neck allows, and snaps when dread is high.
      const over = 0.9 + dread * 0.9; // beyond a natural turn
      const snap = dread > 0.55 && Math.sin(t * 7) > 0.99 ? 0.7 : 0;
      neck.rotation.y = over + snap + 0.03 * Math.sin(t * 0.5);
      // Eerily shallow breathing.
      body.scale.y = 1 + 0.006 * Math.sin(t * 1.1);
      skin.emissiveIntensity = 0.2 + dread * 0.35;
      body.position.z = dread * 0.06;
    },
  };
}

// =========================================================================
// limbs — a writhing knot of too many arms reaching out
// =========================================================================

function buildLimbs(): CreepyAvatar {
  const group = new THREE.Group();
  const skin = flesh(SKIN_GREY, BRUISE, 0.3);

  // A central knotted mass, low to the ground.
  const core = new THREE.Group();
  core.position.y = 0.7;
  group.add(core);
  const mass = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 1), skin);
  mass.scale.set(1, 0.85, 1);
  core.add(mass);

  // A lipless maw splitting the knot of arms, ringed with teeth — it gnashes.
  const maw = new THREE.Mesh(
    new THREE.SphereGeometry(0.1, 12, 10),
    new THREE.MeshStandardMaterial({ color: 0x180404, roughness: 1, emissive: BLOOD, emissiveIntensity: 0.3 }),
  );
  maw.scale.set(1.2, 0.5, 0.6);
  maw.position.set(0, 0.02, 0.18);
  core.add(maw);
  const mawTeethTop = teeth(0.18, 9, 0.02, 0x9a8a76);
  mawTeethTop.position.set(0, 0.05, 0.24);
  core.add(mawTeethTop);
  const mawTeethBot = teeth(0.16, 8, 0.018, 0x8a7a66);
  mawTeethBot.rotation.z = Math.PI;
  mawTeethBot.position.set(0, -0.01, 0.24);
  core.add(mawTeethBot);

  // A dozen arms erupting in all directions, each a pivot we can writhe.
  const arms: { pivot: THREE.Group; phase: number; reach: number }[] = [];
  const N = 12;
  for (let i = 0; i < N; i++) {
    const pivot = new THREE.Group();
    const yaw = (i / N) * Math.PI * 2;
    const pitch = -0.3 + (i % 3) * 0.5; // staggered heights
    pivot.rotation.order = "YXZ";
    pivot.rotation.y = yaw;
    pivot.rotation.x = pitch;

    // Upper arm.
    const upper = capsule(0.035, 0.34, skin);
    upper.position.set(0, 0, 0.2);
    upper.rotation.x = Math.PI / 2;
    pivot.add(upper);
    // Forearm bent at the elbow (a second pivot).
    const elbow = new THREE.Group();
    elbow.position.set(0, 0, 0.4);
    elbow.rotation.x = -0.8 + Math.random() * 0.4;
    pivot.add(elbow);
    const fore = capsule(0.03, 0.3, skin);
    fore.position.set(0, 0, 0.18);
    fore.rotation.x = Math.PI / 2;
    elbow.add(fore);
    // A grasping hand of splayed fingers at the end.
    const hand = new THREE.Group();
    hand.position.set(0, 0, 0.34);
    for (let f = 0; f < 5; f++) {
      const finger = capsule(0.01, 0.06, skin);
      const a = (f - 2) * 0.4;
      finger.position.set(Math.sin(a) * 0.04, 0, 0.04);
      finger.rotation.x = Math.PI / 2 - 0.5;
      finger.rotation.z = a;
      hand.add(finger);
    }
    elbow.add(hand);

    core.add(pivot);
    arms.push({ pivot, phase: i * 0.7, reach: pitch });
  }

  // The knot rests on the floor.
  group.add(groundShadow(0.5));

  return {
    group,
    update: (t, dread) => {
      const speed = 1 + dread * 2.5;
      mass.rotation.y = t * 0.2;
      core.scale.setScalar(1 + 0.04 * Math.sin(t * 2));
      // Every arm writhes on its own phase; they reach further with dread.
      for (const a of arms) {
        a.pivot.rotation.x = a.reach + 0.35 * Math.sin(t * speed + a.phase) - dread * 0.3;
        a.pivot.rotation.z = 0.2 * Math.sin(t * speed * 0.7 + a.phase * 1.3);
      }
      // The maw gnashes, gaping wider and faster as dread rises.
      const gnash = 0.5 + 0.5 * Math.sin(t * (2.5 + dread * 4));
      maw.scale.y = 0.4 + (0.2 + dread * 0.3) * gnash;
      mawTeethBot.position.y = -0.01 - (0.02 + dread * 0.03) * gnash;
      skin.emissiveIntensity = 0.25 + dread * 0.5 + 0.08 * Math.sin(t * 4);
      group.position.z = dread * 0.14;
    },
  };
}

// =========================================================================
// voice — no body. A disembodied presence: one tiny flickering point.
// =========================================================================

function buildVoice(): CreepyAvatar {
  const group = new THREE.Group();

  // A single faint mote hovering at head height — almost nothing.
  const mote = new THREE.Mesh(
    new THREE.SphereGeometry(0.015, 8, 8),
    new THREE.MeshStandardMaterial({ color: 0x000000, emissive: 0xb09070, emissiveIntensity: 0.5 }),
  );
  mote.position.y = 1.55;
  group.add(mote);

  // A second, fainter mote that circles the first — the voice never sits still,
  // it moves around where your ear would be.
  const mote2 = new THREE.Mesh(
    new THREE.SphereGeometry(0.01, 8, 8),
    new THREE.MeshStandardMaterial({ color: 0x000000, emissive: 0x8a6e50, emissiveIntensity: 0.3 }),
  );
  mote2.position.set(0.12, 1.55, 0);
  group.add(mote2);

  // A barely-there glow so it's a presence, not a void.
  const light = new THREE.PointLight(0x9a8466, 0.18, 1.0, 2);
  light.position.set(0, 1.55, 0);
  group.add(light);

  const mat = mote.material as THREE.MeshStandardMaterial;
  const mat2 = mote2.material as THREE.MeshStandardMaterial;

  return {
    group,
    update: (t, dread) => {
      // It flickers like a guttering whisper — erratic, never steady. Dread
      // makes it stutter faster and drift toward the listener.
      const flick =
        0.3 +
        0.4 * Math.abs(Math.sin(t * (5 + dread * 9))) *
          (Math.sin(t * 17.3) > -0.2 ? 1 : 0.1); // dropouts
      mat.emissiveIntensity = flick * (0.6 + dread);
      light.intensity = 0.1 + flick * 0.25 + dread * 0.2;
      mote.position.x = 0.03 * Math.sin(t * 0.8);
      mote.position.y = 1.55 + 0.04 * Math.sin(t * 1.3);
      // Second mote orbits, brightening out of phase — a wandering whisper.
      const orbit = 0.14 + dread * 0.05;
      mote2.position.set(Math.cos(t * 1.1) * orbit, 1.55 + Math.sin(t * 1.7) * 0.06, Math.sin(t * 1.1) * orbit);
      mat2.emissiveIntensity = (0.6 - flick) * (0.5 + dread);
      group.position.z = dread * 0.1;
    },
  };
}

// =========================================================================
// steamform — a translucent whitish humanoid condensed from steam
// =========================================================================

function buildSteamform(): CreepyAvatar {
  const group = new THREE.Group();
  const steam = new THREE.MeshStandardMaterial({
    color: 0xeaf0f2,
    roughness: 1,
    metalness: 0,
    emissive: 0xcfe0e6,
    emissiveIntensity: 0.2,
    transparent: true,
    opacity: 0.25,
    depthWrite: false,
  });

  const body = new THREE.Group();
  group.add(body);

  // Soft, blobby humanoid — overlapping spheres so edges dissolve.
  const torso = new THREE.Mesh(new THREE.SphereGeometry(0.22, 16, 16), steam);
  torso.scale.set(1, 1.7, 0.9);
  torso.position.y = 0.95;
  body.add(torso);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 16, 16), steam);
  head.position.y = 1.45;
  body.add(head);

  // Wisps for arms that trail off into nothing.
  const wisps: THREE.Mesh[] = [];
  for (const sx of [-1, 1]) {
    const arm = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.7, 12), steam);
    arm.rotation.z = sx * (Math.PI - 0.3);
    arm.position.set(sx * 0.22, 1.0, 0);
    wisps.push(arm);
    body.add(arm);
  }
  // A column of vapour instead of legs.
  const base = new THREE.Mesh(new THREE.ConeGeometry(0.26, 0.9, 14), steam);
  base.position.y = 0.45;
  wisps.push(base);
  body.add(base);

  // Two faint hollows for eyes — slightly more opaque, slightly emissive.
  const eyeMat = new THREE.MeshStandardMaterial({
    color: 0xaccdd6,
    emissive: 0x6f9aa6,
    emissiveIntensity: 0.4,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
  });
  for (const sx of [-1, 1]) {
    const e = new THREE.Mesh(new THREE.SphereGeometry(0.025, 10, 10), eyeMat);
    e.position.set(sx * 0.05, 1.47, 0.13);
    body.add(e);
  }

  return {
    group,
    update: (t, dread) => {
      // It boils and condenses — opacity and scale roil. Dread makes it
      // solidify (more opaque) and surge forward.
      const roil = 0.5 + 0.5 * Math.sin(t * 1.5);
      steam.opacity = 0.18 + dread * 0.35 + 0.08 * roil;
      steam.emissiveIntensity = 0.15 + dread * 0.4 + 0.1 * roil;
      eyeMat.emissiveIntensity = 0.3 + dread * 0.7;
      body.position.y = 0.03 * Math.sin(t * 0.9);
      body.scale.set(1 + 0.05 * roil, 1 + 0.03 * Math.sin(t * 2), 1 + 0.05 * roil);
      // Wisps drift upward and sway.
      for (let i = 0; i < wisps.length; i++) {
        wisps[i].rotation.y = 0.2 * Math.sin(t * 1.2 + i);
      }
      group.position.z = dread * 0.16;
    },
  };
}

// =========================================================================
// dispatch
// =========================================================================

export function buildArchetype(name: Archetype): CreepyAvatar {
  switch (name) {
    case "face":
      return buildFace();
    case "burnt":
      return buildBurntMan();
    case "child":
      return buildChild();
    case "tall":
      return buildTall();
    case "crawler":
      return buildCrawler();
    case "shadow":
      return buildShadow();
    case "false_bather":
      return buildFalseBather();
    case "limbs":
      return buildLimbs();
    case "voice":
      return buildVoice();
    case "steamform":
      return buildSteamform();
    default: {
      // Exhaustiveness guard: if a new Archetype is added, this errors at
      // compile time.
      const _never: never = name;
      throw new Error(`unknown archetype: ${String(_never)}`);
    }
  }
}
